import {
  AdjustmentBreakdown,
  AdjustmentFeature,
  OrderInput,
  PairedCostAdjustmentType,
  PairedCostResults,
  PairedCostVersion,
  Path,
  PropertyTypes,
  ReportJSON,
  Tkv2PairedCostSettings,
  Tkv2Property,
  isDefined,
} from '@aloft/toolkit-shared';
import { ProductColor, TypographyVariant } from '@aloft/toolkit-sui';

import { PublicRecords } from '../../controller/types.ts';
import { Tkv2MarketConditionsState } from '../marketConditions/types.ts';
import { ZillowHomeValueIndexTrendsData } from '../marketConditions/zillow/types.ts';
import { PairedCostAdjustmentSummaryProps } from './PairedCostAdjustmentSummary/types.ts';

export type BreakdownColumn = SingleColumn<'feature' | 'adjustment'> | CombinedColumn | SubRows;

export type SingleColumn<T extends 'feature' | 'adjustment'> = ColumnDefinition &
  ColumnDataExtractionDefinition<T>;

export type CombinedColumn = {
  columns: ColumnDataExtractionDefinition<'feature' | 'adjustment'>[];
  combiner: (values: unknown[]) => unknown;
} & ColumnDefinition;

type ColumnDefinition = {
  header: string;
  valueFormatter?: ValueFormatter;
  textAlign?: 'right' | 'left';
  includeTooltip?: boolean;
  cellColorFormatter?: (
    props: Tkv2PairedCostSummaryInterfaceV2 & { compId?: string },
  ) => ProductColor | undefined;
  typographyVariant?: TypographyVariant;
};

export type ColumnDataExtractionDefinition<T extends 'feature' | 'adjustment'> = {
  adjustmentType: PairedCostAdjustmentType;
  type: T;
  path: T extends 'feature' ? Path<AdjustmentFeature> : Path<AdjustmentBreakdown>;
};

type SubRow = {
  header: string;
  label: string;
  columns: (CombinedColumn | SingleColumn<'feature' | 'adjustment'>)[];
  shouldShowForProperty: (summary: Tkv2PairedCostSummaryInterfaceV2, compId?: string) => boolean;
};

type SubRows = {
  subRows: SubRow[];
};

export function isCombinedColumn(column: BreakdownColumn): column is CombinedColumn {
  const col = column as CombinedColumn;
  return isDefined(col.columns) && isDefined(col.combiner);
}
export function isSingleColumn(
  column: BreakdownColumn,
): column is SingleColumn<'feature' | 'adjustment'> {
  const col = column as SingleColumn<'feature' | 'adjustment'>;
  return isDefined(col.adjustmentType) && isDefined(col.type) && isDefined(col.path);
}

export function isSubRows(column: BreakdownColumn): column is SubRows {
  const col = column as SubRows;
  return isDefined(col.subRows);
}

export type ValueFormatter = (val: any) => { str: string[]; color?: ProductColor };

export type ExportableCommentary = {
  commentary: string;
  updateCommentary: (content: string) => void;
  omitFromExport?: boolean;
  toggleOmitFromExport: (val: boolean) => void;
};

export type BreakdownCategory =
  | Exclude<
      PairedCostAdjustmentType,
      'functionalUtility' | 'dateOfSale' | 'concessions' | 'termsOfSale' | 'externalObsolescence'
    >
  | 'allImprovements'
  | 'market';

export type PropertyMetadata = {
  address: string;
  salePrice: string;
};

export type Tkv2PairedCostSummaryInterfaceV2 = {
  compSequence: Array<string>;
  propertyTypes: PropertyTypes[];
  subjectMetadata: PropertyMetadata;
  compMetadata: Record<string, PropertyMetadata>;
  results: PairedCostResults;
  reportJSON: ReportJSON;
  pairedCostSettings: Tkv2PairedCostSettings;
  isPairedCostSettingsLoading: boolean;
  savePairedCostSettings: (next: Tkv2PairedCostSettings, saveAsDefault: boolean) => Promise<void>;

  exportableCommentaries: { [key in BreakdownCategory | 'costApproach']: ExportableCommentary };
  marketConditions: Tkv2MarketConditionsState;
  housingTrends:
    | {
        filteredHousingTrendsData: ZillowHomeValueIndexTrendsData;
        postalCode: string | undefined;
      }
    | undefined;
  subject: Tkv2Property;
  comps: Record<string, Tkv2Property>;
  orderInput: OrderInput | undefined;
  orderID: string;
  accountID: string;
  pairedCostVersion: PairedCostVersion;
  setPairedCostVersion: (version: PairedCostVersion) => void;

  adjustmentSummaryProps: PairedCostAdjustmentSummaryProps;
  publicRecords: { isLoading: boolean; data?: PublicRecords };
};
